import { Component, OnInit, ElementRef, Input, ContentChild, AfterContentInit, Output, EventEmitter, TemplateRef } from '@angular/core';
import { SelectedFile } from 'src/app/models/file-upload';
import { getBase64 } from 'src/app/helpers/helpers';

@Component({
  selector: 'file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss']
})
export class FileUploaderComponent implements OnInit, AfterContentInit {
  @Input() maxSize: number;
  @Input() allowedTypes: [string];
  @Input() fileInput: ElementRef;
  @Input() preview = true;
  @Input() clearable = true;
  @Input() initialValue: any;
  @Input() disabled = false;

  @Output() select = new EventEmitter<SelectedFile>();
  @Output() remove = new EventEmitter<void>();
  @Output() error = new EventEmitter<string[]>();

  @ContentChild('file') fileUpload: ElementRef;
  @ContentChild('placeholder') placeholder: TemplateRef<void>;
  @ContentChild('errorholder') errorholder: TemplateRef<void>;

  UploadError = UploadError;
  Status = Status;

  file: File = null;
  url: string = null;
  status: Status = Status.Idle;
  errors: UploadError[] = [];

  constructor() { }

  ngOnInit() {
    if (this.initialValue) {
      this.file = this.initialValue;
      this.updateThumb();
    }
  }

  ngAfterContentInit() {
    this.input.addEventListener('change', this.onSelect.bind(this));
  }

  onClick(): boolean {
    if (this.disabled) {
      return;
    }

    if (this.file) {
      if (!this.clearable) { return; }
      this.clear();
    } else {
      this.input.click();
    }
    return false;
  }

  onSelect(event: any): void {
    if (this.disabled) {
      return;
    }

    if (event.target.files && event.target.files[0]) {
      this.file = event.target.files[0];

      this.errors = this.validateFile(this.file);
      if (this.errors.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        this.error.emit(this.errors);
        this.clear();
      } else {
        this.updateThumb();
        getBase64(this.file).then(
          data => {
            this.select.emit({
              file: this.file,
              base64: data
            });
          }
        );
      }
    } else {
      this.clear();
    }
  }

  clear() {
    this.file = null;
    this.url = null;
    this.input.value = null;
    this.remove.emit();
  }

  validateFile(file: File): UploadError[] {
    const errors = [];
    if (!this.validateFileSize(file)) {
      errors.push(UploadError.Size);
    }

    if (!this.validateFileType(file)) {
      errors.push(UploadError.Type);
    }

    return errors;
  }

  validateFileSize(file: File): boolean {
    if (this.maxSize && file.size / 1024 > this.maxSize) {
      return false;
    }

    return true;
  }

  validateFileType(file: File): boolean {
    if (this.allowedTypes && !this.allowedTypes.includes(this.getFileType(file))) {
      return false;
    }

    return true;
  }

   getFileType(file) {
    const type: string = file.name.slice(file.name.lastIndexOf('.') + 1);
    return type ? type.toLowerCase() : null;
   }

  updateThumb(): void {
    if (!this.preview) { return; }

    const type = this.getFileType(this.file);

    if (!['jpg', 'jpeg', 'png'].includes(type)) {
      this.url = 'assets/images/icons/success.svg';
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(this.file);
    reader.onload = (e: any) => {
      this.url = e.target.result;
    };
  }

  get input() {
    if (this.fileInput) {
      return this.fileInput.nativeElement;
    }
    return this.fileUpload.nativeElement;
  }

  onDragOver(event: any) {
    event.preventDefault();
    event.stopPropagation();

    this.status = Status.Active;
  }

  onDragLeave(event: any) {
    event.preventDefault();
    event.stopPropagation();

    this.status = Status.Idle;
  }

  onDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();

    this.status = Status.Idle;

    if (event.dataTransfer.files) {
      this.input.files = event.dataTransfer.files;
    }
  }
}

enum Status {
  Idle = 'idle',
  Active = 'active'
}

enum UploadError {
  Size = 'error_size',
  Type = 'error_type'
}
